import { useEffect, useRef, useState } from 'react';
import { moveArrayItem } from '../../utils/move-array-item';

export const useDynamicObjectList = <T extends { id?: I }, I = number>(
  initialList: T[]
) => {
  const [list, setList] = useState<T[]>(initialList);

  const add = (newItem: T) => {
    setList(prevState => {
      if (prevState.find(item => item.id === newItem.id)) {
        return prevState;
      }
      return [
        ...prevState,
        newItem
      ];
    });
  };

  const remove = (id: I) => setList(prevState => prevState.filter(i => i.id != id));

  const update = (item: T, key: keyof T = 'id') => setList(prevState => prevState.map(i => (item[key] === i[key] ? item : i)));

  const upsert = (item: T, key: keyof T = 'id') => {
    if (list.some(i => item[key] === i[key])) {
      update(item, key);
    } else {
      add(item);
    }
  };

  const move = (itemIndex: number, newPosition: number) => setList(moveArrayItem(list, itemIndex, newPosition));

  const get = (id: I) => list.find(i => i.id === id);

  const has = (id: I) => list.find(i => i.id === id) !== undefined;

  const toggle = (item: T) => {
    if (has(item.id!)) {
      remove(item.id!);
    } else {
      add(item);
    }
  };

  const addAll = (items: T[]) => {
    items.forEach(add);
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setList(initialList);
  }, [JSON.stringify(initialList)]);

  return {
    list,
    addAll,
    set: setList,
    add,
    remove,
    upsert,
    move,
    update,
    toggle,
    get,
    has,
  };
};